// components/admin/Admin.jsx
import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Users,
  Search,
  Edit,
  Trash2,
  Eye,
  Shield,
  Settings,
  KeyRound,
  UserPlus2,
  ChevronLeft,
  ChevronRight,
  Database,
} from "lucide-react";
import { adminService } from "../../apis/adminService";
import { subscriptionService } from "../../apis/subscriptionService";
import {
  AddUserModal,
  UpdateUserModal,
  DeleteUserModal,
  PasswordUpdateModal,
} from "./UserFormModal";
import { ICD10CodeUpload } from "./ICD10CodeUpload";
import { MedicationUpload } from "./MedicationUpload";
import toast from "react-hot-toast";
import UserProfile from "./UserProfile";
import { PlatformLimitsSettings } from "./PlatformLimitsSettings";
import { useSignedUrlRetry, shareInflight } from "../../hooks/useSignedUrlRetry";

// Signed avatar URLs expire: on load failure refetch the user list once, then show the initials (children).
function UserListAvatar({ imageUrl, alt, refetchUrl, children }) {
  const { src, refreshing, onError } = useSignedUrlRetry(imageUrl, refetchUrl);
  if (!src || refreshing) return children;
  return <img src={src} alt={alt} className="h-full w-full object-cover" onError={onError} />;
}

export function Admin() {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [addModal, setAddModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [passwordModal, setPasswordModal] = useState(false);
  const [filterRole, setFilterRole] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [message, setMessage] = useState(null);
  const [message1, setMessage1] = useState(null);
  const [message2, setMessage2] = useState(null);
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);

  const availableProducts = ["Professional", "Enterprise"];
  const availablePermissions = ["read", "write", "delete"];
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [filterAct, setFilterAct] = useState("active");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const rootRef = useRef(null);
  // Scroll offset of the user list, saved when a profile is opened so we can restore it on "Back".
  const savedScrollRef = useRef(null);

  // The page scrolls inside a layout container (not window), so find the nearest scrollable ancestor.
  const getScrollParent = () => {
    let el = rootRef.current?.parentElement;
    while (el) {
      const { overflowY } = window.getComputedStyle(el);
      if (/(auto|scroll)/.test(overflowY) && el.scrollHeight > el.clientHeight) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  };

  // Restore the list's previous scroll position after returning from a profile.
  useLayoutEffect(() => {
    if (!showUserProfile && savedScrollRef.current !== null) {
      const top = savedScrollRef.current;
      savedScrollRef.current = null;
      const container = getScrollParent();
      if (container) container.scrollTop = top;
    }
  }, [showUserProfile]);

  // Clicking the "Admin Panel" settings tab (even while it is already active) while a
  // profile is open returns to the user list, same as "Back to Users".
  useEffect(() => {
    const handleAdminTabClick = () => {
      setShowUserProfile(false);
      setSelectedUser(null);
    };
    window.addEventListener("admin-panel-tab-click", handleAdminTabClick);
    return () =>
      window.removeEventListener("admin-panel-tab-click", handleAdminTabClick);
  }, []);

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    admins: 0,
  });

  const ROLE_CONSTANTS = {
    ADMIN: { id: "2", label: "Admin", value: "admin" },
    USER: { id: "1", label: "User", value: "user" },
    SCRIBE_FULLTIME: {
      id: "4",
      label: "scribe_fulltime",
      value: "scribe_fulltime",
    },
    SCRIBE_PARTTIME: {
      id: "3",
      label: "scribe_parttime",
      value: "scribe_parttime",
    },
  };

  const ROLE_META = {
    admin: {
      label: "Admin",
      badge: "bg-blue-200 text-blue-800",
      deletable: false,
    },
    user: {
      label: "User",
      badge: "bg-gray-200 text-gray-800",
      deletable: true,
    },
    scribe_fulltime: {
      label: "Scribe (Full-Time)",
      badge: "bg-teal-200 text-teal-800",
      deletable: true,
    },
    scribe_parttime: {
      label: "Scribe (Part-Time)",
      badge: "bg-yellow-200 text-yellow-800",
      deletable: true,
    },
  };

  const resolveUserRole = (user) => {
    if (user.role?.role) return user.role.role;

    if (user.roleid) {
      switch (String(user.roleid)) {
        case "2":
          return "admin";
        case "1":
          return "user";
        case "4":
          return "scribe_fulltime";
        case "3":
          return "scribe_parttime";
        default:
          return "user";
      }
    }

    return "user";
  };

  const handleRowClick = (user, event) => {
    if (!event.target.closest("button")) {
      savedScrollRef.current = getScrollParent()?.scrollTop ?? window.scrollY;
      setSelectedUser(user);
      setShowUserProfile(true);
    }
  };

  const handleBackToUsers = () => {
    setShowUserProfile(false);
    setSelectedUser(null);
  };

  // ------------------------------------------------------------------
  // Subscription display for the table row
  // ------------------------------------------------------------------
  const SubscriptionDisplay = ({ user }) => {
    const sub = user?.subscription;

    if (!sub) {
      return (
        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full border border-gray-200">
          —
        </span>
      );
    }

    // Plan resolution — prefer planKey, fall back to plan_type
    const planKeyRaw =
      sub.planKey ||
      sub.plan_key ||
      sub.paymentInfo?.planKey ||
      sub.latestPayment?.planKey ||
      null;

    const planTypeLower = String(
      sub.plan_type || sub.planType || ""
    ).toLowerCase();
    const planKeyLower = String(planKeyRaw || "").toLowerCase();

    let planLabel;
    let planColor;

    if (planKeyLower === "starter") {
      planLabel = "Starter";
      planColor = "bg-blue-100 text-blue-800 border-blue-200";
    } else if (
      planKeyLower === "professional" ||
      planKeyLower === "pro"
    ) {
      planLabel = "Professional";
      planColor = "bg-yellow-100 text-yellow-800 border-yellow-200";
    } else if (planKeyLower === "individual") {
      planLabel = "Individual";
      planColor = "bg-emerald-100 text-emerald-800 border-emerald-200";
    } else if (planKeyLower === "custom") {
      planLabel = "Custom";
      planColor = "bg-purple-100 text-purple-800 border-purple-200";
    } else if (planKeyLower === "group") {
      planLabel = "Group";
      planColor = "bg-purple-100 text-purple-800 border-purple-200";
    } else if (planKeyLower === "enterprise") {
      planLabel = "Enterprise";
      planColor = "bg-indigo-100 text-indigo-800 border-indigo-200";
    } else if (planKeyLower === "free") {
      planLabel = "Free";
      planColor = "bg-gray-100 text-gray-700 border-gray-200";
    } else if (planTypeLower === "premium") {
      planLabel = "Professional";
      planColor = "bg-yellow-100 text-yellow-800 border-yellow-200";
    } else if (planTypeLower === "free") {
      planLabel = "Free";
      planColor = "bg-gray-100 text-gray-700 border-gray-200";
    } else if (planKeyRaw) {
      planLabel = String(planKeyRaw)
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
      planColor = "bg-gray-100 text-gray-700 border-gray-200";
    } else {
      planLabel = "Free";
      planColor = "bg-gray-100 text-gray-700 border-gray-200";
    }

    // Status resolution
    const statusRaw = String(sub.status || "active").toLowerCase();
    const isTrial =
      sub.isTrial ||
      sub.is_trial ||
      statusRaw === "trial" ||
      statusRaw === "trialing";

    let statusLabel;
    let statusColor;
    if (isTrial) {
      statusLabel = "Trial";
      statusColor = "bg-blue-100 text-blue-800";
    } else if (statusRaw === "active") {
      statusLabel = "Active";
      statusColor = "bg-teal-100 text-teal-800";
    } else if (statusRaw === "expired") {
      statusLabel = "Expired";
      statusColor = "bg-red-100 text-red-800";
    } else if (statusRaw === "canceled" || statusRaw === "cancelled") {
      statusLabel = "Canceled";
      statusColor = "bg-orange-100 text-orange-800";
    } else {
      statusLabel =
        statusRaw.charAt(0).toUpperCase() + statusRaw.slice(1);
      statusColor = "bg-gray-100 text-gray-700";
    }

    return (
      <div className="flex flex-col items-center gap-1">
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${planColor}`}
        >
          {planLabel}
        </span>
        <span
          className={`px-2.5 py-0.5 rounded-full text-xs font-medium uppercase tracking-wide ${statusColor}`}
        >
          {statusLabel}
        </span>
      </div>
    );
  };

  const fetchUser = async (signal) => {
    const isFirstLoad = users.length === 0;
    if (isFirstLoad) setLoadingUsers(true);
    try {
      const res = await adminService.getuser(
        {
          page,
          limit,
          search: searchTerm,
          role: filterRole,
          status: filterAct,
        },
        { signal }
      );

      if (signal?.aborted) return;
      if (res.success) {
        setUsers(res.data.users || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 0);
      } else {
        console.error("Failed to fetch users:", res.message);
        setUsers([]);
        setTotal(0);
        setTotalPages(0);
      }
    } catch (err) {
      if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
      console.error("Fetch error:", err);
      setUsers([]);
    } finally {
      if (!signal?.aborted) setLoadingUsers(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await adminService.getstats();
      if (res.success) {
        setStats(res.data);
      }
    } catch (err) {
      console.error("Stats fetch error:", err);
    }
  };

  // An avatar's signed URL expired: re-request the same page of the list (without touching the
  // table state) and pick this user's fresh URL. Rows that fail together share one request.
  const refetchUserAvatarUrl = async (userId) => {
    const params = { page, limit, search: searchTerm, role: filterRole, status: filterAct };
    const res = await shareInflight(`admin-users:${JSON.stringify(params)}`, () =>
      adminService.getuser(params)
    );
    const fresh = res?.success
      ? res.data?.users?.find((u) => u.userId === userId)
      : null;
    return fresh?.profile?.profileimage || null;
  };

  useEffect(() => {
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetchUser(controller.signal);
    }, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [page, limit, searchTerm, filterRole, filterAct]);

  useEffect(() => {
    fetchStats();
  }, []);

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 rounded-full text-xs font-medium";
    return status === "active"
      ? `${baseClasses} bg-teal-100 text-teal-800`
      : `${baseClasses} bg-red-200 text-red-800`;
  };

  const getRoleBadge = (role) => {
    const base = "px-2 py-1 rounded-full text-xs font-medium";
    return `${base} ${ROLE_META[role]?.badge || ROLE_META.user.badge}`;
  };

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  const validateName = (name) => {
    const regex = /^[A-Za-z\s]+$/;
    return regex.test(name.trim());
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "2",
    status: "1",
    products: [],
    permissions: [],
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fOk = validateName(formData.firstName);
    const lOk = validateName(formData.lastName);

    setFirstNameError(
      fOk ? null : { type: "error", text: "numeric values are not allowed" }
    );
    setLastNameError(
      lOk ? null : { type: "error", text: "numeric values are not allowed" }
    );

    if (!fOk || !lOk) return;
    try {
      const result = await adminService.adduser({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        role: formData.role,
        products: formData.products,
        permissions: formData.permissions,
      });

      if (result.success) {
        setMessage2({ type: "success", text: result.message });
        toast.success("User added successfully");
      } else {
        if (result.message === "User already exists") {
          setMessage2({ type: "error", text: "User already exists" });
          toast.error("User already exists");
          return;
        } else {
          setMessage2({
            type: "error",
            text: "Something went wrong. Please try again.",
          });
          toast.error("Something went wrong. Please try again.");
        }
      }
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        role: "2",
        status: "1",
        products: [],
        permissions: [],
      });

      await fetchUser();
      setAddModal(false);
    } catch (error) {
      setMessage({
        type: "error",
        text: "User Added Failed Please try again.",
      });
      toast.error("User Added Failed Please try again.");
    }
  };

  const [updateFormData, setUpdateFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    status: "",
    products: [],
    permissions: [],
  });

  const openUpdateModal = (user) => {
    setUpdateFormData({
      firstName: user.profile?.firstName || "",
      lastName: user.profile?.lastName || "",
      email: user.email || "",
      role: String(user.roleid?.roleid ?? user.roleid),
      status: user?.isActive != null ? (user.isActive ? "1" : "0") : "1",
      products: user.products || [],
      permissions: user.permissions || [],
      userId: user.userId,
    });
    setUpdateModal(true);
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    const fOk = validateName(updateFormData.firstName);
    const lOk = validateName(updateFormData.lastName);

    setFirstNameError(
      fOk ? null : { type: "error", text: "numeric values are not allowed" }
    );
    setLastNameError(
      lOk ? null : { type: "error", text: "numeric values are not allowed" }
    );

    if (!fOk || !lOk) return;
    try {
      const result = await adminService.updateuser(updateFormData.userId, {
        firstName: updateFormData.firstName,
        lastName: updateFormData.lastName,
        email: updateFormData.email,
        roleid: updateFormData.role,
        isActive: updateFormData.status === "1",
        products: updateFormData.products,
        permissions: updateFormData.permissions,
      });

      if (result.success) {
        setMessage({ type: "success", text: result.message });
        toast.success("User updated successfully");
      } else {
        setMessage({ type: "error", text: result.message });
        toast.error(result.message || "Failed to update user");
      }

      await fetchUser();
      setUpdateModal(false);
    } catch (error) {
      setMessage({
        type: "error",
        text: "User update failed. Please try again.",
      });
      toast.error("User update failed. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    if (deletingUser) return;
    setDeletingUser(true);
    try {
      const result = await adminService.deleteuser(id);
      if (result.success) {
        setMessage({ type: "success", text: result.message });
        toast.success("User deleted successfully");
      } else {
        setMessage({ type: "error", text: result.message });
        toast.error(result.message || "Failed to delete user");
      }
    } catch (error) {
      console.error("Delete error:", error);
      setMessage({ type: "error", text: "Something went wrong" });
      toast.error("Something went wrong");
    } finally {
      setDeletingUser(false);
      setDeleteModal(false);
      await fetchUser();
    }
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () =>
    setShowConfirmPassword(!showConfirmPassword);

  const handlePasswordInputChange = (field) => (e) => {
    const value = e.target.value;
    setPasswordFormData((prev) => ({ ...prev, [field]: value }));
  };

  const [passwordFormData, setPasswordFormData] = useState({
    password: "",
    confirmPassword: "",
  });

  const openPasswordModal = (user) => {
    setPasswordFormData({
      userId: user.userId,
      password: "",
      confirmPassword: "",
    });
    setPasswordModal(true);
  };

  const validatePassword = (password) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (passwordFormData.password !== passwordFormData.confirmPassword) {
      setMessage1({ type: "error", text: "passwords should match" });
      return;
    }
    if (!validatePassword(passwordFormData.password)) {
      setMessage1({
        type: "error",
        text: "passwords requirements are not matched",
      });
      return;
    }
    try {
      const result = await adminService.updatepassworduser({
        id: passwordFormData.userId,
        password: passwordFormData.confirmPassword,
      });

      if (result.success) {
        setMessage1({ type: "success", text: result.message });
        toast.success("Password updated successfully");
      } else {
        setMessage1({ type: "error", text: result.message });
        toast.error(result.message || "Failed to update password");
      }
      setPasswordModal(false);
    } catch (error) {
      setMessage1({
        type: "error",
        text: "Password update failed. Please try again.",
      });
      toast.error("Password update failed. Please try again.");
    }
  };

  return (
    <div ref={rootRef} className="min-h-screen bg-gray-50 p-3 sm:p-6">
      {showUserProfile && selectedUser ? (
        <UserProfile
          user={selectedUser}
          onBack={handleBackToUsers}
          adminService={adminService}
        />
      ) : (
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-4 sm:mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">
              Admin Panel
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Manage users and access permissions for VirtualScribe products
            </p>
          </div>

          <PlatformLimitsSettings />

          <div className="mb-6 sm:mb-8">
            <ICD10CodeUpload />
          </div>

          <div className="mb-6 sm:mb-8">
            <MedicationUpload />
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
            <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-2 border-gray-200">
              <div className="flex items-center">
                <Users className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">
                    Total Users
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">
                    {stats.totalUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-2 border-gray-200">
              <div className="flex items-center">
                <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-teal-500" />
                <div className="ml-3">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">
                    Active Users
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">
                    {stats.activeUsers}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4 sm:p-6 border-2 border-gray-200">
              <div className="flex items-center">
                <Settings className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
                <div className="ml-3">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">
                    Admins
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">
                    {stats.admins}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-white rounded-lg shadow mb-4 sm:mb-6 border-2 border-gray-300">
            <div className="p-4 sm:p-6">
              <div className="flex flex-col gap-3 sm:gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search users by name or email"
                      className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border-2 border-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <div className="flex gap-4">
                    <label className="flex items-center text-sm">
                      <input
                        type="radio"
                        id="active"
                        name="act"
                        value="active"
                        checked={filterAct === "active"}
                        onChange={(e) => setFilterAct(e.target.value)}
                        className="mr-2 cursor-pointer w-4 h-4 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-teal-600">Active</span>
                    </label>
                    <label className="flex items-center text-sm">
                      <input
                        type="radio"
                        id="inactive"
                        name="act"
                        value="inactive"
                        checked={filterAct === "inactive"}
                        onChange={(e) => setFilterAct(e.target.value)}
                        className="mr-2 cursor-pointer w-4 h-4 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-red-600">Inactive</span>
                    </label>
                  </div>

                  <div className="flex gap-2 sm:gap-3">
                    <select
                      className="px-3 sm:px-4 py-2 text-sm sm:text-base border-2 border-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer flex-1"
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                    >
                      <option value="all">All Roles</option>
                      <option value="admin">Admin</option>
                      <option value="user">User</option>
                      <option value="scribe_fulltime">
                        Scribe (Full-Time)
                      </option>
                      <option value="scribe_parttime">
                        Scribe (Part-Time)
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead className="bg-gray-200 border-b border-gray-500">
                  <tr>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-800 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-800 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-800 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-800 uppercase tracking-wider">
                      Subscription
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-800 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-center text-xs font-medium text-gray-800 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingUsers ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-8 text-center">
                        <div className="flex justify-center items-center">
                          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                          <span className="ml-2 text-gray-600">
                            Loading users…
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : users.length > 0 ? (
                    users.map((user) => {
                      const fullName = `${user.profile?.firstName || ""} ${
                        user.profile?.lastName || ""
                      }`.trim();

                      return (
                        <tr
                          key={user.userId}
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={(e) => handleRowClick(user, e)}
                        >
                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                                <UserListAvatar
                                  imageUrl={user.profile?.profileimage}
                                  alt={fullName}
                                  refetchUrl={() => refetchUserAvatarUrl(user.userId)}
                                >
                                  <span className="text-xs sm:text-sm font-medium text-gray-700">
                                    {fullName
                                      ? `${fullName.split(" ")[0][0] || ""}${
                                          fullName.split(" ")[1]?.[0] || ""
                                        }`
                                      : user.email[0]}
                                  </span>
                                </UserListAvatar>
                              </div>

                              <div className="ml-3 min-w-0">
                                <div className="text-sm font-medium text-gray-900 truncate">
                                  {fullName || "N/A"}
                                </div>
                                <div className="text-xs sm:text-sm text-gray-500 truncate">
                                  {user.email}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-center">
                            {(() => {
                              const role = resolveUserRole(user);
                              return (
                                <span className={getRoleBadge(role)}>
                                  {ROLE_META[role]?.label || "User"}
                                </span>
                              );
                            })()}
                          </td>

                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-center">
                            <span
                              className={getStatusBadge(
                                user.isActive ? "active" : "inactive"
                              )}
                            >
                              {user.isActive ? "Active" : "Inactive"}
                            </span>
                          </td>

                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-center">
                            <SubscriptionDisplay user={user} />
                          </td>

                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-center text-xs sm:text-sm">
                            {user.lastLogin
                              ? new Date(user.lastLogin).toLocaleDateString()
                              : "Never"}
                          </td>

                          <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-center">
                            <div
                              className="flex justify-center gap-1 sm:gap-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => openUpdateModal(user)}
                                className="text-blue-600 hover:text-blue-900 p-1"
                                aria-label="Edit user"
                              >
                                <Edit className="h-4 w-4" />
                              </button>

                              {user.role?.role !== "admin" &&
                                user.roleid !== 2 && (
                                  <button
                                    onClick={() =>
                                      setDeleteModal(user.userId)
                                    }
                                    className="text-red-600 hover:text-red-900 p-1"
                                    aria-label="Delete user"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}

                              {(user.role?.role === "admin" ||
                                user.roleid === 2) && (
                                <button
                                  disabled
                                  className="text-gray-300 p-1 cursor-not-allowed"
                                  aria-label="Delete disabled for admin users"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-8 text-center text-gray-600"
                      >
                        <div className="flex flex-col items-center justify-center">
                          <Users className="h-12 w-12 text-gray-400 mb-2" />
                          <p className="text-lg font-medium">No users found</p>
                          <p className="text-sm text-gray-500">
                            Try adjusting your search or filters
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-3 sm:px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs sm:text-sm text-gray-700 order-1">
                {total > 0 ? (
                  <>
                    Showing {(page - 1) * limit + 1} to{" "}
                    {Math.min(page * limit, total)} of {total} results
                  </>
                ) : (
                  <>Showing 0 to 0 of 0 results</>
                )}
              </div>

              <div className="flex items-center gap-2 order-3 sm:order-2">
                <span className="text-xs sm:text-sm text-gray-600 whitespace-nowrap">
                  Rows per page:
                </span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="border-2 border-gray-400 rounded px-2 py-1 text-xs sm:text-sm cursor-pointer"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <div className="flex items-center gap-1 sm:gap-2 order-2 sm:order-3">
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className="p-1 sm:p-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                {getPageNumbers().map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-2 py-1 text-xs sm:text-sm rounded min-w-[2rem] ${
                      page === p
                        ? "bg-blue-600 text-white"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={page === totalPages}
                  className="p-1 sm:p-2 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {updateModal && (
        <UpdateUserModal
          fname={updateFormData.firstName}
          lname={updateFormData.lastName}
          email={updateFormData.email}
          role={updateFormData.role}
          status={updateFormData.status}
          onChangeFName={(e) =>
            setUpdateFormData((prev) => ({
              ...prev,
              firstName: e.target.value,
            }))
          }
          onChangeLName={(e) =>
            setUpdateFormData((prev) => ({ ...prev, lastName: e.target.value }))
          }
          onChangeEmail={(e) =>
            setUpdateFormData((prev) => ({ ...prev, email: e.target.value }))
          }
          onChangeRole={(e) =>
            setUpdateFormData((prev) => ({ ...prev, role: e.target.value }))
          }
          onChangeStatus={(e) =>
            setUpdateFormData((prev) => ({ ...prev, status: e.target.value }))
          }
          onToggleProduct={(product) => {
            setUpdateFormData((prev) => ({
              ...prev,
              products: prev.products.includes(product)
                ? prev.products.filter((p) => p !== product)
                : [...prev.products, product],
            }));
          }}
          onClose={() => {
            setUpdateModal(false);
            setFirstNameError(null);
            setLastNameError(null);
          }}
          handleSubmit={handleUpdateSubmit}
          firstNameError={firstNameError}
          lastNameError={lastNameError}
        />
      )}

      {deleteModal && (
        <DeleteUserModal
          deleting={deletingUser}
          cancelDelete={() => {
            if (!deletingUser) setDeleteModal(null);
          }}
          confirmDelete={() => handleDelete(deleteModal)}
        />
      )}
    </div>
  );
}